/**
 * ============================================================================
 * USE THEME HOOK
 * ============================================================================
 * 
 * Custom hook for theme management (light/dark mode)
 */

import { useState, useEffect, useCallback } from 'react';

const THEME_STORAGE_KEY = 'wekatrust-theme';
const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

/**
 * Get system theme preference
 */
const getSystemTheme = () => {
  if (typeof window === 'undefined') return THEMES.LIGHT;
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? THEMES.DARK
    : THEMES.LIGHT;
};

/**
 * Get stored theme or default
 */
const getStoredTheme = () => {
  if (typeof window === 'undefined') return THEMES.SYSTEM;
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) || THEMES.SYSTEM;
  } catch {
    return THEMES.SYSTEM;
  }
};

/**
 * Get effective theme (resolves system theme)
 */
const getEffectiveTheme = (theme) => {
  if (theme === THEMES.SYSTEM) {
    return getSystemTheme();
  }
  return theme;
};

export const useTheme = (initialTheme = null) => {
  const [theme, setThemeState] = useState(() => {
    return initialTheme || getStoredTheme();
  });
  const [effectiveTheme, setEffectiveTheme] = useState(() =>
    getEffectiveTheme(initialTheme || getStoredTheme())
  );

  /**
   * Apply theme to document
   */
  const applyTheme = useCallback((newTheme) => {
    const effective = getEffectiveTheme(newTheme);
    const root = document.documentElement;
    
    if (effective === THEMES.DARK) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    
    root.setAttribute('data-theme', effective);
    setEffectiveTheme(effective);
  }, []);

  /**
   * Set theme
   */
  const setTheme = useCallback(
    (newTheme) => {
      setThemeState(newTheme);
      try {
        localStorage.setItem(THEME_STORAGE_KEY, newTheme);
      } catch (err) {
        console.error('Failed to save theme:', err);
      }
      applyTheme(newTheme);
    },
    [applyTheme]
  );

  /**
   * Toggle between light and dark
   */
  const toggleTheme = useCallback(() => {
    const currentEffective = getEffectiveTheme(theme);
    const newTheme = currentEffective === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    setTheme(newTheme);
  }, [theme, setTheme]);

  /**
   * Initialize theme on mount
   */
  useEffect(() => {
    const stored = getStoredTheme();
    if (!initialTheme) {
      setThemeState(stored);
    }
    applyTheme(stored);
  }, [initialTheme, applyTheme]);

  /**
   * Listen for system theme changes
   */
  useEffect(() => {
    if (theme !== THEMES.SYSTEM) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme(THEMES.SYSTEM);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [theme, applyTheme]);

  return {
    theme,
    effectiveTheme,
    setTheme,
    toggleTheme,
    isDark: effectiveTheme === THEMES.DARK,
    isLight: effectiveTheme === THEMES.LIGHT,
    themes: THEMES,
  };
};

export default useTheme;
